import type { RevokeThirdPartyContactAccessMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { RevokeThirdPartyContactAccessDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * `ThirdPartyId` has no V2 counterpart — the legacy action took bare
 * `ContactIds` and gated on an org-level permission. The tRPC procedure scopes
 * both the lookup and the update by it (404 on a mismatch), so callers must
 * thread the third party through. The Apollo branch drops it: the legacy
 * mutation has no such variable.
 */
export interface RevokeThirdPartyContactAccessInput {
  ThirdPartyId: string;
  ContactIds: string[];
}

interface RevokeResult {
  Id: string;
  IsRevoked: boolean;
  Message: string;
}

/**
 * `revokeThirdPartyContactAccess` (COI-170). Folded onto `createMutationHook`
 * because the data-layer processor uses `NoOpEventStrategy`, so the tRPC
 * service is a plain call — no `executeAsyncRequest`, no twin needed.
 */
export const useRevokeThirdPartyContactAccess = createMutationHook({
  name: 'revokeThirdPartyContactAccess',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.thirdPartyContact.revokeAccess.mutationOptions({ onSuccess }),
  mapInput: (input: RevokeThirdPartyContactAccessInput) => input,
  mapResponse: (result: {
    results: RevokeResult[];
  }): RevokeThirdPartyContactAccessMutation => ({
    revokeThirdPartyContactAccess: {
      __typename: 'RevokeThirdPartyContactAccessOutput',
      results: result.results.map((contactResult) => ({
        __typename: 'RevokeThirdPartyContactAccessResultOutput',
        Id: contactResult.Id,
        IsRevoked: contactResult.IsRevoked,
        Message: contactResult.Message,
      })),
    },
  }),
  // Apollo parity: evictField(cache, 'third_party_contact')
  invalidate: (trpc) => [trpc.frontend.thirdPartyContact.pathKey()],
  graphqlDocument: RevokeThirdPartyContactAccessDocument,
  graphqlVariables: (input) => ({ ContactIds: input.ContactIds }),
  evictFields: ['third_party_contact'],
});
